import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Edit2, MapPin, Clock, Target, Gauge, MessageSquare, Sparkles, Users, ArrowLeft, User as UserIcon } from "lucide-react";
import AppShell from "@/components/app/AppShell";
import { IconAvatar } from "@/components/app/IconAvatar";
import Heatmap from "@/components/app/Heatmap";
import { getProfile } from "@/lib/storage";
import { getOwnProfileHeatmap, getPublicProfile, getPublicProfileHeatmap } from "@/lib/api";
import { partnershipIntentLabel } from "@/onboarding/data";

export default function ProfileView({ embedded = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [publicProfile, setPublicProfile] = useState(null);
  const [ownHeatmap, setOwnHeatmap] = useState({});
  const [publicHeatmap, setPublicHeatmap] = useState({});
  const [loading, setLoading] = useState(Boolean(id));

  useEffect(() => { setMe(getProfile()); }, []);

  useEffect(() => {
    if (!(embedded || !id)) return;
    let cancelled = false;
    getOwnProfileHeatmap()
      .then((response) => {
        if (!cancelled) setOwnHeatmap(response?.heatmap || {});
      })
      .catch(() => {
        if (!cancelled) setOwnHeatmap({});
      });
    return () => { cancelled = true; };
  }, [embedded, id]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([getPublicProfile(id), getPublicProfileHeatmap(id)])
      .then(([profileResponse, heatmapResponse]) => {
        if (cancelled) return;
        setPublicProfile(profileResponse);
        setPublicHeatmap(heatmapResponse?.heatmap || {});
      })
      .catch(() => {
        if (!cancelled) setPublicProfile(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [id]);

  const isOwn = embedded || !id;
  const profile = isOwn ? me : publicProfile;

  if (!me && isOwn) return null; // waiting for local profile load

  if (loading) {
    const loadingView = <div className="p-10 max-w-4xl mx-auto text-sm text-[#A1A1AA]">Loading profile...</div>;
    return embedded ? loadingView : <AppShell>{loadingView}</AppShell>;
  }

  if (!profile) {
    const notFound = (
        <div className="p-10 max-w-4xl mx-auto">
          <button onClick={() => navigate(-1)} className="text-sm text-[#A1A1AA] hover:text-[#F5F5F0] inline-flex items-center gap-2 mb-6"><ArrowLeft className="w-4 h-4" /> Back</button>
          <div className="hm-card p-8 rounded-md">
            <h2 className="font-serif-editorial text-2xl mb-2">Profile not found.</h2>
            <p className="text-[#A1A1AA] font-light">This profile may have been removed or your link is stale.</p>
          </div>
        </div>
    );
    return embedded ? notFound : <AppShell>{notFound}</AppShell>;
  }

  const nonNeg = (profile.idealDay || []).filter((b) => (profile.nonNegotiableBlocks || []).includes(b.id));
  const otherBlocks = (profile.idealDay || []).filter((b) => !(profile.nonNegotiableBlocks || []).includes(b.id));

  // Both views use server-authoritative completion history. The own endpoint
  // includes personal tasks plus non-imported partnership tasks without
  // double-counting tasks imported from the personal task board.
  const heatmapData = isOwn
    ? ownHeatmap
    : publicHeatmap;

  const content = (
      <div className="px-6 sm:px-10 py-10 max-w-5xl mx-auto">
        {!isOwn && (
          <button onClick={() => navigate(-1)} className="text-sm text-[#A1A1AA] hover:text-[#F5F5F0] inline-flex items-center gap-2 mb-6" data-testid="pv-back">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        )}
        {/* Header */}
        <div className="hm-card p-8 rounded-md mb-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-6">
            <div className="flex items-start gap-5">
              <IconAvatar iconKey={profile.profileIcon} size="xl" />
              <div className="min-w-0">
                <div className="overline mb-2">{isOwn ? "Your profile" : "Match profile"}</div>
                <h1 className="font-serif-editorial font-light tracking-tighter text-4xl sm:text-5xl leading-[1.02]">{profile.fullName}</h1>
                <div className="text-xs text-[#A1A1AA] flex flex-wrap items-center gap-x-3 gap-y-1 mt-2">
                  <span>@{profile.username}</span>
                  <span>· {profile.age}</span>
                  {(profile.gender && profile.gender !== "Prefer not to say") && <span>· {profile.gender === "Custom" ? profile.customGender : profile.gender}</span>}
                  <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {[profile.city, profile.country].filter(Boolean).join(", ") || "—"}</span>
                  <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {profile.timezone || "—"}</span>
                </div>
                {profile.bio && <p className="mt-4 text-sm text-[#F5F5F0] font-light leading-relaxed max-w-2xl">{profile.bio}</p>}
              </div>
            </div>
            {isOwn && (
              <Link to="/onboarding" data-testid="pv-edit" className="hm-btn-ghost inline-flex items-center gap-2 px-4 py-2 rounded-md text-xs self-start">
                <Edit2 className="w-3.5 h-3.5" /> Edit profile
              </Link>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {/* Consistency heatmap — full width */}
          <div className="md:col-span-2 hm-card p-6 rounded-md" data-testid="pv-heatmap">
            <Heatmap
              data={heatmapData}
              weeks={52}
              cellSize={18}
              title={isOwn ? "Your consistency" : `${profile.fullName.split(" ")[0]}'s consistency`}
              subtitle="Task completions per day · last 12 months"
            />
          </div>

          <Card icon={Target} title="Goals">
            <div className="text-sm mb-3">{profile.goalDescription || "—"}</div>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {(profile.goalTags || []).map((t) => (
                <span key={t} className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-sm border border-[#F59E0B]/30 bg-[#F59E0B]/5 text-[#F59E0B]">{t}</span>
              ))}
            </div>
            <Stat label="Level" value={profile.level} />
            <Stat label="Target" value={profile.target} />
            <Stat label="By" value={profile.targetDate} />
            <Stat label="Importance" value={`${profile.importance} / 10`} />
          </Card>

          <Card icon={Gauge} title="Work Style">
            <Stat label="Chronotype" value={cap(profile.chronotype)} />
            <Stat label="Daily hours" value={`${profile.dailyHours}h`} />
            <Stat label="Focus length" value={profile.focusLength} />
            <Stat label="Methods" value={(profile.productivityMethods || []).join(", ")} />
            <Stat label="Challenges" value={(profile.challenges || []).join(", ")} />
          </Card>

          <Card icon={Clock} title="Schedule">
            <Stat label="Working days" value={(profile.workingDays || []).join(" · ")} />
            <Stat label="Available" value={`${profile.availableStart}–${profile.availableEnd}`} />
            <Stat label="Frequency" value={profile.meetingFrequency} />
            <Stat label="Duration" value={profile.sessionDuration} />
            <Stat label="Communication" value={(profile.communication || []).join(", ")} />
          </Card>

          <Card icon={Sparkles} title="Accountability">
            <Stat label="Styles" value={(profile.accountabilityStyle || []).join(", ")} />
            <Stat label="If I ghost" value={cap(profile.disappearAction)} />
            <Stat label="Motivation" value={profile.motivationType} />
            <Stat label="Strictness" value={`${profile.strictness} / 10`} />
            <Stat label="Honesty" value={`${profile.honesty} / 10`} />
          </Card>

          <Card icon={Users} title="Partner preferences">
            <Stat label="Connection intent" value={partnershipIntentLabel(profile.partnershipIntent)} />
            <Stat label="Looking for" value={profile.lookingFor} />
            <Stat label="Level" value={profile.partnerLevel} />
            <Stat label="Age range" value={profile.ageRange?.join("–")} />
            <Stat label="Gender" value={profile.partnerGender} />
            <Stat label="Languages" value={(profile.languagesPreferred || []).join(", ")} />
            <Stat label="Deal breakers" value={(profile.dealBreakers || []).join(", ")} />
          </Card>

          <Card icon={UserIcon} title="Personality & Commitment">
            <Sliders profile={profile} />
            <Stat label="Interests" value={(profile.interests || []).join(", ")} />
            {profile.quote && <div className="mt-3 text-sm italic text-[#A1A1AA] font-serif-editorial">"{profile.quote}"</div>}
            <Stat label="Commitment" value={profile.commitmentDuration} />
            <Stat label="Active partnerships" value={profile.activePartnerships} />
          </Card>

          <div className="md:col-span-2 hm-card p-6 rounded-md">
            <div className="flex items-center gap-2 mb-4">
              <MessageSquare className="w-4 h-4 text-[#F59E0B]" strokeWidth={1.5} />
              <div className="overline">Ideal day</div>
            </div>
            {(profile.idealDay || []).length === 0 ? (
              <div className="text-sm text-[#A1A1AA] italic">Not specified.</div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                {nonNeg.map((b) => (
                  <BlockCard key={b.id} block={b} nonNeg />
                ))}
                {otherBlocks.map((b) => (
                  <BlockCard key={b.id} block={b} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
  );
  return embedded ? content : <AppShell>{content}</AppShell>;
}

function Card({ icon: Icon, title, children }) {
  return (
    <div className="hm-card p-6 rounded-md">
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-4 h-4 text-[#F59E0B]" strokeWidth={1.5} />
        <div className="overline">{title}</div>
      </div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}
function Stat({ label, value }) {
  if (value == null || value === "") return null;
  return (
    <div className="grid grid-cols-[110px_1fr] gap-3 text-sm">
      <div className="text-[#A1A1AA] font-light">{label}</div>
      <div className="text-[#F5F5F0] font-light break-words">{value}</div>
    </div>
  );
}
function Sliders({ profile }) {
  const pairs = [
    ["introExtro", "Introvert", "Extrovert"],
    ["plannerSpont", "Planner", "Spontaneous"],
    ["compCoop", "Competitive", "Cooperative"],
    ["seriousFun", "Serious", "Fun"],
    ["optRealist", "Optimistic", "Realistic"],
    ["logicEmo", "Logical", "Emotional"],
  ];
  return (
    <div className="space-y-2 mb-3">
      {pairs.map(([k, l, r]) => (
        <div key={k}>
          <div className="flex justify-between text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA]">
            <span>{l}</span><span>{r}</span>
          </div>
          <div className="relative h-1 bg-white/10 rounded-full mt-1">
            <div className="absolute -top-1 w-3 h-3 rounded-full bg-[#F59E0B]" style={{ left: `calc(${((profile[k] || 5) - 1) * 11}% - 6px)` }} />
          </div>
        </div>
      ))}
    </div>
  );
}
function BlockCard({ block, nonNeg }) {
  return (
    <div className={`p-3 rounded-md border ${nonNeg ? "border-[#F59E0B]/40 bg-[#F59E0B]/5" : "border-white/10 bg-[#0f0f0f]"}`}>
      <div className="text-xs font-serif-editorial text-[#F59E0B]">{block.start} → {block.end}</div>
      <div className="text-sm mt-1">{block.label}</div>
      {nonNeg && <div className="text-[9px] uppercase tracking-[0.2em] text-[#F59E0B] mt-1">Non-negotiable</div>}
    </div>
  );
}
function cap(s) { if (!s) return "—"; return s.charAt(0).toUpperCase() + s.slice(1); }
